import React, { useState } from "react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { EVOLUTION } from "@/data/investments";

export default function InvestmentEvolution() {
  useReveal();
  const [active, setActive] = useState(0);
  const current = EVOLUTION[active];

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow="Investment / Evolution"
        title="From foundation to"
        accent={<><br/>ecosystem maturity.</>}
        sub="Unik's investment evolution reflects a strategic maturity journey — building core assets, developing platforms, scaling validated models, and institutionalising the ecosystem."
        bgImage="/assets/9eitpmqk_hanoi-building.jpg"
      />

      <section
        className="relative bg-[#f3f4f6] py-28 lg:py-36"
        data-testid="evolution-tabs"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="max-w-3xl mb-14 lg:mb-16">
            <div className="eyebrow text-[#14355a]">Investment Evolution</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.05",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              A strategic maturity journey.
            </h2>
            <p className="mt-7 text-[18px] lg:text-[19px] leading-[1.95] text-unik-ink/80 max-w-2xl">
              Unik&apos;s investment evolution reflects a strategic maturity
              journey: building core assets, developing platforms, scaling
              validated models, and institutionalising the ecosystem.
            </p>
          </div>

          {/* Progress bar tabs */}
          <div className="relative" data-reveal>
            <div className="absolute top-[18px] left-0 right-0 h-px bg-[#14355a]/15 z-0" />
            <div
              className="absolute top-[18px] left-0 h-[2px] bg-unik-gold z-10 transition-all duration-700 ease-out"
              style={{ width: `${((active + 1) / EVOLUTION.length) * 100}%` }}
            />
            <div className="relative z-10 grid grid-cols-4 gap-2 lg:gap-4">
              {EVOLUTION.map((t, i) => (
                <button
                  key={t.phase}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`evolution-tab-${i}`}
                  className="group text-left"
                >
                  <div className="flex items-center justify-start">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-[13px] transition-all duration-500 ${
                        i <= active
                          ? "bg-unik-gold text-[#14355a]"
                          : "bg-white border border-[#14355a]/20 text-[#14355a]/55"
                      }`}
                      style={{ fontWeight: 600 }}
                    >
                      {i + 1}
                    </div>
                  </div>
                  <div
                    className={`mt-5 text-[13px] lg:text-[14px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                      active === i ? "text-unik-gold" : "text-[#14355a]/60"
                    }`}
                  >
                    {t.phase}
                  </div>
                  <div
                    className={`mt-3 font-display tracking-tight transition-colors duration-300 ${
                      active === i ? "text-[#14355a]" : "text-[#14355a]/65 group-hover:text-[#14355a]"
                    }`}
                    style={{
                      fontSize: "clamp(1.25rem, 1.85vw, 1.625rem)",
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    {t.title}
                  </div>
                  <div className="mt-2 text-[14px] lg:text-[15px] text-unik-ink/65 hidden md:block">
                    {t.when}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Active phase content */}
          <div
            key={active}
            className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 fade-in"
            data-testid="evolution-content"
          >
            <div className="lg:col-span-6">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={current.img}
                  alt={current.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0e2540]/45 via-transparent to-transparent" />
                <div className="absolute top-6 left-6">
                  <span
                    className="inline-block px-4 py-1.5 bg-unik-gold text-[#14355a] font-display text-[10.5px] uppercase tracking-[0.22em]"
                    style={{ fontWeight: 600 }}
                  >
                    {current.phase}
                  </span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="eyebrow text-unik-gold">{current.when}</div>
              <h3
                className="mt-5 font-display text-[#14355a] tracking-tight"
                style={{
                  fontSize: "clamp(2rem, 3vw, 2.75rem)",
                  fontWeight: 500,
                  lineHeight: 1.1,
                }}
              >
                {current.title}
              </h3>
              <p className="mt-7 text-[16px] lg:text-[17px] leading-[1.95] text-unik-ink/80">
                {current.focus}
              </p>
              <div className="mt-8 pt-7 border-t border-[#14355a]/15">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">
                  Capital Allocation
                </div>
                <div
                  className="mt-4 space-y-2 text-[15px] lg:text-[16px] text-black leading-[1.65]"
                  style={{ fontWeight: 500 }}
                >
                  {current.capital.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow="Continue Reading"
        title="Explore our"
        accent="portfolio."
        body="From foundational real assets to flagship wellness estates — see the operating real assets at the heart of our portfolio."
        primary={{ label: "Portfolio Assets", to: "/investments/portfolio-assets" }}
        secondary={{ label: "Platforms", to: "/investments/platforms" }}
      />

      <SiteFooter />
    </div>
  );
}
